// src/views/reports/SalesReportView.js

import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../../firebase/config';
import { collection, query, onSnapshot, doc } from 'firebase/firestore';
import { Link } from 'react-router-dom';
import { ArrowLeft, TrendingUp, Download, Award, ExternalLink, Calendar } from 'lucide-react';
import { startOfMonth, endOfMonth, format, parseISO } from 'date-fns';
import { formatCurrency } from '../../utils/formatters';
import { exportToCSV } from '../../utils/csvExport';
// qreport-5b8e2c64 (Quote Seam 12a) — this report is INVOICES ONLY now. Its
// estimate win rate counted losses as status 'Rejected', which no estimate
// ever has, so it read 100% whenever anything was won (BACKLOG). Founder
// decision 2026-10-02: remove the estimate numbers and link to the Estimates
// report, which counts from pipeline stages — one place for estimate numbers.
// Gone with them: the quotes/services/settings-company listeners and the
// estimate drill-down + editor modal, which nothing else here used.
import { DOC_TYPES, resolveDocTerminology } from '../../utils/quoteSchema'; // r1-p1-docterm-sales-b4e7c2f9

import CustomerFormModal from '../../components/crm/CustomerFormModal';
import { grossInvoiceTotal, grossCashCollected, creditNotesInPeriod } from '../../utils/invoiceNetRevenue'; // netrev-6f2a8c53 (R1d-5) - net of credit notes; cnperiod-5a3e7d92 - a credit note counts in the period of its own date

const SalesReportView = ({ currentUserProfile }) => {
    const [invoices, setInvoices] = useState([]);
    const [customers, setCustomers] = useState([]);
    // r1-p1-docterm-sales-b4e7c2f9
    // Org-default doc terminology (Quotes/Estimates) for the aggregate report labels —
    // now only the wording of the link to the Estimates report. Read off the ORG DOC
    // (organizations/{orgId}), where CompanySettings writes quoteTerminologyDefault.
    const [quoteTerminologyDefault, setQuoteTerminologyDefault] = useState(DOC_TYPES.QUOTE);
    const [loading, setLoading] = useState(true);

    // Modal State
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);

    // Date Range
    const [startDate, setStartDate] = useState(format(startOfMonth(new Date()), 'yyyy-MM-dd'));
    const [endDate, setEndDate] = useState(format(endOfMonth(new Date()), 'yyyy-MM-dd'));

    useEffect(() => {
        if (!currentUserProfile?.organizationId) return;
        const orgId = currentUserProfile.organizationId;

        // 1. Fetch ALL Invoices (Accrual Basis needs everything, not just Paid)
        const unsubInvoices = onSnapshot(query(collection(db, `organizations/${orgId}/invoices`)), (snap) => {
            setInvoices(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setLoading(false);
        });

        // 2. Helpers
        const unsubCust = onSnapshot(collection(db, `organizations/${orgId}/customers`), (snap) =>
            setCustomers(snap.docs.map(d => ({ id: d.id, ...d.data() })))
        );
        // r1-p1-docterm-sales-b4e7c2f9 — org-default terminology off the ORG doc (not settings/company)
        const unsubTerm = onSnapshot(doc(db, `organizations/${orgId}`), (d) => {
            if (d.exists()) setQuoteTerminologyDefault(d.data()?.quoteTerminologyDefault || DOC_TYPES.QUOTE);
        });

        return () => { unsubInvoices(); unsubCust(); unsubTerm(); }; // r1-p1-docterm-sales-b4e7c2f9
    }, [currentUserProfile?.organizationId]);

    // --- METRICS ENGINE ---
    const metrics = useMemo(() => {
        const start = parseISO(startDate);
        const end = parseISO(endDate);
        end.setHours(23, 59, 59, 999);

        // CUSTOMER / INVOICE ANALYSIS (estimate analysis moved — qreport-5b8e2c64)
        const getInvoiceDate = (inv) => {
            if (inv.createdAt?.toDate) return inv.createdAt.toDate();
            if (inv.issuedAt?.toDate) return inv.issuedAt.toDate();
            if (inv.date) return new Date(inv.date);
            return new Date(); 
        };

        const filteredInvoices = invoices.filter(inv => {
            if (['Draft', 'Void', 'Cancelled'].includes(inv.status)) return false;
            const d = getInvoiceDate(inv);
            inv.dateObj = d;
            return d >= start && d <= end;
        });

        const clientRevenueMap = {};
        let totalSalesAccrual = 0; // NEW: Total Invoiced
        let totalCashCollected = 0; // NEW: Actual Cash

        filteredInvoices.forEach(inv => {
            const name = inv.clientName || inv.customerName || 'Unknown';
            // cnperiod-5a3e7d92 -- the invoice's own total; credit notes come off below, by THEIR date.
            const amount = grossInvoiceTotal(inv);
            
            // 1. Accrual Calculation (Total Sales)
            totalSalesAccrual += amount;

            // 2. Cash Calculation (Revenue Collected)
            // If Paid: Full amount. If Partial: AmountPaid.
            // The same rule as ever (Paid: the total; otherwise amountPaid). cnperiod-5a3e7d92 --
            // money handed back against credit notes comes off below, by the credit note's date.
            const cashFromThisInvoice = grossCashCollected(inv);
            totalCashCollected += cashFromThisInvoice;

            // 3. Client Ranking (Based on Sales Volume)
            const cid = inv.customerId; 
            if (!clientRevenueMap[name]) clientRevenueMap[name] = { total: 0, id: cid };
            clientRevenueMap[name].total += amount;
            if (!clientRevenueMap[name].id && cid) clientRevenueMap[name].id = cid;
        });

        // cnperiod-5a3e7d92 -- credit notes ISSUED in these dates, whatever month their invoice was
        // billed in: off sales, off cash (the money went back then) and off that customer's figure.
        // None for a company that has never issued one: every figure above is then untouched.
        const countedInvoices = invoices.filter(inv => !['Draft', 'Void', 'Cancelled'].includes(inv.status));
        creditNotesInPeriod(countedInvoices, start, end, getInvoiceDate).forEach(c => {
            totalSalesAccrual -= c.amount;
            totalCashCollected -= c.amount;
            const name = c.invoice.clientName || c.invoice.customerName || 'Unknown';
            if (!clientRevenueMap[name]) clientRevenueMap[name] = { total: 0, id: c.invoice.customerId };
            clientRevenueMap[name].total -= c.amount;
        });

        const topClients = Object.entries(clientRevenueMap)
            .map(([name, data]) => ({ name, total: data.total, id: data.id }))
            .sort((a, b) => b.total - a.total)
            .slice(0, 10); 

        return {
            totalSalesAccrual, // Total Invoiced
            totalCashCollected, // Cash Received
            topClients
        };
    }, [invoices, startDate, endDate]);

    // Handlers
    // r1-p1-docterm-sales-b4e7c2f9 — resolve org-default word/plural for aggregate report labels
    const { wordPlural: docWordPlural } = resolveDocTerminology(quoteTerminologyDefault);
    const handleCustomerClick = (clientName, clientId) => {
        let customer = customers.find(c => c.id === clientId) || customers.find(c => (c.firstName + ' ' + c.lastName) === clientName);
        if (customer) { setSelectedCustomer(customer); setIsCustomerModalOpen(true); }
        else alert(`Could not find a full profile for "${clientName}".`);
    };
    const handleExport = () => {
        if (!metrics.topClients.length) return alert("No client data to export");
        const csvData = metrics.topClients.map(c => ({ Customer: c.name, Total_Revenue: c.total.toFixed(2) }));
        exportToCSV(csvData, `Sales_Revenue_By_Client_${startDate}.csv`);
    };

    // --- FIX: REMOVED HARDCODED PERMISSION CHECK ---
    
    if (loading) return <div className="p-10 text-center text-gray-500">Crunching sales numbers...</div>;

    return (
        <div className="p-6 md:p-10 bg-gray-50 min-h-screen">
            {/* HEADER */}
            <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-6 gap-4">
                <div className="flex items-center gap-4">
                    <Link to="/reports" className="p-2 bg-white border rounded-full hover:bg-gray-100">
                        <ArrowLeft size={20} className="text-gray-600" />
                    </Link>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-800">Sales Performance</h1>
                        <p className="text-gray-500 text-sm">Invoiced sales, cash collected and top customers</p>
                    </div>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                    <div className="flex items-center gap-2 bg-white p-2 rounded-md border shadow-sm">
                        <Calendar size={18} className="text-gray-400" />
                        <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="text-sm border-none focus:ring-0 text-gray-600" />
                        <span className="text-gray-400">-</span>
                        <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="text-sm border-none focus:ring-0 text-gray-600" />
                    </div>
                    <button onClick={handleExport} className="flex items-center gap-2 bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-50 transition-colors shadow-sm">
                        <Download size={18} /> Export
                    </button>
                </div>
            </div>

            {/* KPI CARDS (Updated Order) */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                
                {/* 1. TOTAL SALES (ACCRUAL) */}
                <div className="bg-white p-6 rounded-lg border shadow-sm border-l-4 border-l-blue-500">
                    <p className="text-sm text-gray-500 mb-1">Total Sales</p>
                    <h3 className="text-2xl font-bold text-gray-900">{formatCurrency(metrics.totalSalesAccrual)}</h3>
                    <p className="text-xs text-gray-400 mt-1">Total invoiced volume</p>
                </div>

                {/* 2. REVENUE COLLECTED (CASH) */}
                <div className="bg-white p-6 rounded-lg border shadow-sm">
                    <p className="text-sm text-gray-500 mb-1">Revenue Collected</p>
                    <h3 className="text-2xl font-bold text-green-600">{formatCurrency(metrics.totalCashCollected)}</h3>
                    <p className="text-xs text-gray-400 mt-1">Actual cash received</p>
                </div>

                {/* qreport-5b8e2c64 — estimate numbers moved to the Estimates report. */}
                <Link
                    to="/reports/estimates"
                    className="md:col-span-2 bg-white p-6 rounded-lg border shadow-sm hover:shadow-md transition-shadow flex items-center justify-between gap-4 group"
                >
                    <div>
                        <p className="text-sm text-gray-500 mb-1 flex items-center gap-2"><Award size={16} className="text-blue-600" /> {docWordPlural} win rate</p>
                        <h3 className="text-lg font-bold text-gray-900 group-hover:text-blue-700">Moved to the {docWordPlural} Report</h3>
                        <p className="text-xs text-gray-500 mt-1">Win rate, open value by stage, and why {docWordPlural.toLowerCase()} were won or lost.</p>
                    </div>
                    <ExternalLink size={18} className="text-gray-400 group-hover:text-blue-600 shrink-0" />
                </Link>
            </div>

            <div className="grid grid-cols-1 gap-8">
                {/* 2. TOP CLIENTS TABLE */}
                <div className="bg-white border rounded-lg shadow-sm overflow-hidden">
                    <div className="p-4 border-b bg-gray-50 flex items-center gap-2">
                        <TrendingUp size={18} className="text-blue-600" />
                        <h3 className="font-bold text-gray-800">Top Customers (Sales Volume)</h3>
                    </div>
                    {metrics.topClients.length === 0 ? (
                        <div className="p-8 text-center text-gray-500">No invoices found in this period.</div>
                    ) : (
                        <table className="w-full text-left">
                            <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                                <tr>
                                    <th className="p-3">Customer</th>
                                    <th className="p-3 text-right">Total Sales</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y text-sm">
                                {metrics.topClients.map((client, index) => (
                                    <tr key={client.name} onClick={() => handleCustomerClick(client.name, client.id)} className="hover:bg-blue-50 cursor-pointer transition-colors group">
                                        <td className="p-3 font-medium flex items-center gap-2">
                                            <span className="text-gray-400 text-xs w-4">#{index + 1}</span>
                                            <span className="group-hover:text-blue-700">{client.name}</span>
                                            <ExternalLink size={12} className="text-gray-300 group-hover:text-blue-400 opacity-0 group-hover:opacity-100" />
                                        </td>
                                        <td className="p-3 text-right font-bold text-gray-700">{formatCurrency(client.total)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {isCustomerModalOpen && <CustomerFormModal closeModal={() => setIsCustomerModalOpen(false)} editingCustomer={selectedCustomer} currentUserProfile={currentUserProfile} customers={customers} user={currentUserProfile} />}
        </div>
    );
};

export default SalesReportView;